/* ==========================================================================
   sd-v4.js - SimDrive v4 Motion: the shared motion system (every page)
   --------------------------------------------------------------------------
   Loaded deferred after sd-chat.js, so it runs after sd-motion.js and before
   sd-v4-home.js. window.sdV4 is defined synchronously, first thing.

   THE INTERFACE (SPEC 3.1 C)
     sdV4.ready            Promise<{gsap, ScrollTrigger} | null>, the same
                           object as window.sdMotionReady (sd-motion.js)
     sdV4.isPhone()        (max-width: 899px)
     sdV4.isStage()        (min-width: 1000px) and (min-height: 640px)
     sdV4.belowFold(el, r) top below r * viewport height (default .88);
                           false for display:none
     sdV4.split(el)        idempotent word splitter; returns the .sd-w__i word
                           inners, the accent word(s) last
     sdV4.slam(el, opts)   E1 word slam, returns a gsap.timeline.
                           opts { immediateRender:false, delay:0, blur:!isPhone() }
                           With immediateRender:false every word writes its own
                           from-state at its own stagger start, so build the
                           slam at the moment it should play (or pass true
                           inside a timeline you scrub).
     sdV4.prepDraw(path)   sets dasharray/offset to the full length now
     sdV4.draw(path, opts) E4 line draw tween. opts { duration:1.2,
                           ease:'power2.inOut', immediateRender:false }
     sdV4.follow(path, el, p)  puts el at progress p (0..1) along path
     sdV4.count(el, opts)  E3 counter tween. opts { from:0, duration:1.1 };
                           always ends on the exact original text
     sdV4.scramble(el, o)  E10, aria-hidden elements only
     sdV4.paused()         the visitor pressed Pause motion (now or stored)
     sdV4.setPaused(bool)  persists, toggles html.sd-v4-paused, dispatches
                           'sd:motion-pause' { detail: { paused } }
     sdV4.headerOffset()   px from --sd-v4-hdr
     sdV4.refresh()        debounced (150 ms) ScrollTrigger.refresh()
   split, slam, prepDraw, draw, follow, count and scramble need GSAP: call
   them inside sdV4.ready.then(function (m) { if (!m) return; ... }).

   DECLARATIVE (run here on every page once ready resolves non-null)
     data-sd-split         heading slams once at top 85%, if below the fold
     data-sd-draw          SVG stroke draws once at top 85%, if below the fold
     data-sd-scramble      aria-hidden mono label scrambles once at top 90%
     data-sd-motion-toggle <button hidden>; unhidden and kept in sync here
                           (label "Pause motion" / "Play motion", no aria-pressed)
     data-sd-axes          the PDP axes strip

   NOTHING IS HIDDEN BEFORE GSAP IS PROVEN. With no JS, reduced motion,
   Save-Data, the stored pause or the theme setting off, this file never
   writes a single pre-state. Headings are split at init but their from-state
   is only written when their trigger fires.
   ========================================================================== */

(function () {
  'use strict';
  if (window.sdV4) return;

  var ROOT = document.documentElement;
  var KEY = 'sd-motion';
  var CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  var COUNT_RE = /^(\D*?)(\d[\d,]*(?:\.\d+)?)([\s\S]*)$/;

  function mq(q) { return window.matchMedia ? window.matchMedia(q) : { matches: false }; }
  var mqPhone = mq('(max-width: 899px)');
  var mqStage = mq('(min-width: 1000px) and (min-height: 640px)');

  var M = null;                 // { gsap, ScrollTrigger } once ready resolved non-null
  var splitCache = new WeakMap();
  var lenCache = new WeakMap();
  var registry = [];            // { el, kill } for section unload in the editor

  function storedPause() {
    try { return localStorage.getItem(KEY) === 'off'; } catch (e) { return false; }
  }
  var loadedPaused = storedPause();
  var pausedNow = false;
  var toggleMode = 'hidden';    // 'run' | 'stored' | 'hidden'

  /* ---------------------------------------------------------------------
     ready: sd-motion.js's promise, or an event-based fallback that also
     settles (null) when motion is plainly not going to run.
     --------------------------------------------------------------------- */
  function fallbackReady() {
    return new Promise(function (resolve) {
      if (window.sdMotionAllowed && !window.sdMotionAllowed()) { resolve(null); return; }
      if (!ROOT.hasAttribute('data-sd-gsap')) { resolve(null); return; }
      if (ROOT.classList.contains('sd-motion-on') && window.gsap && window.ScrollTrigger) {
        resolve({ gsap: window.gsap, ScrollTrigger: window.ScrollTrigger });
        return;
      }
      document.addEventListener('sd:motion-ready', function () {
        resolve({ gsap: window.gsap, ScrollTrigger: window.ScrollTrigger });
      }, { once: true });
      setTimeout(function () { resolve(null); }, 20000);
    });
  }
  var ready = window.sdMotionReady || fallbackReady();

  window.sdV4 = {
    ready: ready,
    isPhone: isPhone,
    isStage: isStage,
    belowFold: belowFold,
    split: split,
    slam: slam,
    prepDraw: prepDraw,
    draw: draw,
    follow: follow,
    count: count,
    scramble: scramble,
    paused: paused,
    setPaused: setPaused,
    headerOffset: headerOffset,
    refresh: refresh
  };

  /* ---------------------------------------------------------------------
     Small helpers
     --------------------------------------------------------------------- */
  function G() { return M ? M.gsap : window.gsap; }
  function ST() { return M ? M.ScrollTrigger : window.ScrollTrigger; }
  function noop() { return G().to({}, { duration: 0 }); }
  function each(root, sel, fn) {
    var list = Array.prototype.slice.call((root || document).querySelectorAll(sel));
    if (root && root.nodeType === 1 && root.matches && root.matches(sel)) list.unshift(root);
    list.forEach(fn);
  }
  function track(el, killable) {
    if (killable) registry.push({ el: el, kill: function () { killable.kill(); } });
    return killable;
  }

  function isPhone() { return mqPhone.matches; }
  function isStage() { return mqStage.matches; }

  function belowFold(el, ratio) {
    if (!el || !el.getBoundingClientRect) return false;
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    return r.top > (window.innerHeight || 800) * (ratio == null ? 0.88 : ratio);
  }

  function headerOffset() {
    var v = parseFloat(getComputedStyle(ROOT).getPropertyValue('--sd-v4-hdr'));
    return isFinite(v) ? v : 0;
  }

  var refreshTimer = 0;
  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      var S = ST();
      if (M && S) S.refresh();
    }, 150);
  }

  /* ---------------------------------------------------------------------
     E2 split. Words become .sd-w > .sd-w__i; whitespace text nodes stay put
     so the lines wrap exactly as before; <br> survives; an accent element
     (.sd-accent) is wrapped whole; punctuation touching a word with no space
     joins that word, so "motion." can never break before the period.
     A heading with no interactive content gets a visually hidden copy of its
     text and an aria-hidden visual layer, so screen readers hear words, not
     fragments. A heading WITH a link or button is split in place instead
     (links recursed into, never wrapped), so nothing focusable is ever
     inside aria-hidden.
     --------------------------------------------------------------------- */
  function textOf(node) {
    var out = '';
    (function walk(n) {
      for (var c = n.firstChild; c; c = c.nextSibling) {
        if (c.nodeType === 3) out += c.nodeValue;
        else if (c.nodeType === 1) {
          if (c.tagName === 'BR') out += ' ';
          else if (c.getAttribute('aria-hidden') !== 'true') walk(c);
        }
      }
    })(node);
    return out.replace(/\s+/g, ' ').trim();
  }

  function mkWord(accent) {
    var w = document.createElement('span');
    w.className = accent ? 'sd-w sd-w--accent' : 'sd-w';
    var i = document.createElement('span');
    i.className = 'sd-w__i';
    w.appendChild(i);
    return w;
  }

  function wrapWords(parent, words, accents) {
    var kids = Array.prototype.slice.call(parent.childNodes);
    var cur = null; // the open word's inner, while no whitespace has been seen
    kids.forEach(function (n) {
      if (n.nodeType === 3) {
        var parts = n.nodeValue.split(/(\s+)/);
        var frag = document.createDocumentFragment();
        parts.forEach(function (p) {
          if (!p) return;
          if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); cur = null; return; }
          if (!cur) {
            var w = mkWord(false);
            frag.appendChild(w);
            cur = w.firstChild;
            words.push(cur);
          }
          cur.appendChild(document.createTextNode(p));
        });
        parent.replaceChild(frag, n);
        return;
      }
      if (n.nodeType !== 1) return;
      if (n.tagName === 'BR') { cur = null; return; }
      if (n.classList.contains('sd-accent')) {
        if (cur) {
          // "(" or similar glued in front of the accent: promote that word.
          cur.parentNode.classList.add('sd-w--accent');
          var at = words.indexOf(cur);
          if (at > -1) { words.splice(at, 1); accents.push(cur); }
          cur.appendChild(n);
        } else {
          var aw = mkWord(true);
          parent.replaceChild(aw, n);
          aw.firstChild.appendChild(n);
          cur = aw.firstChild;
          accents.push(cur);
        }
        return;
      }
      cur = null;
      if (n.classList.contains('sd-u-visually-hidden') || n.getAttribute('aria-hidden') === 'true') return;
      wrapWords(n, words, accents);
      cur = null;
    });
  }

  function split(el) {
    if (!el || el.nodeType !== 1) return [];
    var cached = splitCache.get(el);
    if (cached) return cached;
    var words = [];
    var accents = [];
    if (el.querySelector('a, button, input, select, textarea, [tabindex]')) {
      wrapWords(el, words, accents);
    } else {
      var label = textOf(el);
      var vis = document.createElement('span');
      vis.setAttribute('aria-hidden', 'true');
      while (el.firstChild) vis.appendChild(el.firstChild);
      var sr = document.createElement('span');
      sr.className = 'sd-u-visually-hidden';
      sr.textContent = label;
      el.appendChild(sr);
      el.appendChild(vis);
      wrapWords(vis, words, accents);
    }
    var out = words.concat(accents);
    splitCache.set(el, out);
    el.setAttribute('data-sd-split-done', '');
    return out;
  }

  /* ---------------------------------------------------------------------
     E1 slam. Words rise and un-tilt (expo.out, stagger .06); the accent word
     lands last, from a little larger and soft, overlapping by .45 s.
     --------------------------------------------------------------------- */
  function slam(el, opts) {
    var g = G();
    opts = opts || {};
    var ir = opts.immediateRender === true;
    var blur = opts.blur != null ? !!opts.blur : !isPhone();
    var words = [];
    var acc = [];
    split(el).forEach(function (i) {
      (i.parentNode && i.parentNode.classList.contains('sd-w--accent') ? acc : words).push(i);
    });
    var tl = g.timeline({ delay: opts.delay || 0 });
    if (words.length) {
      tl.fromTo(words,
        { yPercent: 110, rotateX: -35, opacity: 0, transformPerspective: 600, transformOrigin: '50% 100%' },
        { yPercent: 0, rotateX: 0, opacity: 1, duration: 0.7, ease: 'expo.out', stagger: 0.06, immediateRender: ir },
        0);
    }
    if (acc.length) {
      var from = { yPercent: 110, scale: 1.18, opacity: 0 };
      var to = { yPercent: 0, scale: 1, opacity: 1, duration: 0.7, ease: 'expo.out', immediateRender: ir };
      if (blur) { from.filter = 'blur(6px)'; to.filter = 'blur(0px)'; to.clearProps = 'filter'; }
      tl.fromTo(acc, from, to, words.length ? '-=0.45' : 0);
    }
    return tl;
  }

  /* ---------------------------------------------------------------------
     E4 draw / E5 follow. Geometry length (for points along the path) and
     dash length (honours a pathLength attribute) are kept apart.
     --------------------------------------------------------------------- */
  function geomLength(el) {
    var c = lenCache.get(el);
    if (c) return c;
    var L = 0;
    try { L = el.getTotalLength(); } catch (e) { L = 0; }
    if (!(L > 0)) {
      var t = (el.tagName || '').toLowerCase();
      var a = function (n) { return parseFloat(el.getAttribute(n)) || 0; };
      if (t === 'circle') L = 2 * Math.PI * a('r');
      else if (t === 'line') L = Math.hypot(a('x2') - a('x1'), a('y2') - a('y1'));
      else if (t === 'rect') L = 2 * (a('width') + a('height'));
      else if (t === 'polyline' || t === 'polygon') {
        var pts = (el.getAttribute('points') || '').trim().split(/[\s,]+/).map(parseFloat);
        for (var i = 2; i + 1 < pts.length; i += 2) L += Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
      }
    }
    if (L > 0) lenCache.set(el, L);
    return L;
  }

  function dashLength(el) {
    var pl = parseFloat(el.getAttribute('pathLength'));
    return pl > 0 ? pl : geomLength(el);
  }

  function prepDraw(path) {
    if (!path) return 0;
    var L = dashLength(path);
    path.style.strokeDasharray = L + ' ' + L;
    path.style.strokeDashoffset = L;
    return L;
  }

  function draw(path, opts) {
    var g = G();
    opts = opts || {};
    if (!path) return noop();
    var L = dashLength(path);
    return g.fromTo(path,
      { strokeDasharray: L + ' ' + L, strokeDashoffset: L },
      {
        strokeDashoffset: 0,
        duration: opts.duration != null ? opts.duration : 1.2,
        ease: opts.ease || 'power2.inOut',
        immediateRender: opts.immediateRender === true
      });
  }

  function follow(path, el, p) {
    if (!path || !el) return;
    var L = geomLength(path);
    if (!(L > 0)) return;
    p = p < 0 ? 0 : p > 1 ? 1 : (p || 0);
    var pt = path.getPointAtLength(p * L);
    el.setAttribute('transform', 'translate(' + pt.x + ' ' + pt.y + ')');
  }

  /* ---------------------------------------------------------------------
     E3 count. Counts only the first text node that holds a number, so any
     markup around it survives, and always restores the exact original text
     (forward or reversed), so no number is ever left on an untrue value.
     --------------------------------------------------------------------- */
  function firstDigitText(el) {
    var w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    for (var n = w.nextNode(); n; n = w.nextNode()) {
      if (/\d/.test(n.nodeValue)) return n;
    }
    return null;
  }

  function count(el, opts) {
    var g = G();
    opts = opts || {};
    var node = el ? firstDigitText(el) : null;
    if (!node) return noop();
    var text = node.nodeValue;
    var m = text.match(COUNT_RE);
    if (!m) return noop();
    var raw = m[2].replace(/,/g, '');
    var target = parseFloat(raw);
    if (!isFinite(target)) return noop();
    var dec = raw.indexOf('.') > -1 ? raw.split('.')[1].length : 0;
    var fmt = { minimumFractionDigits: dec, maximumFractionDigits: dec, useGrouping: m[2].indexOf(',') > -1 };
    var proxy = { v: opts.from || 0 };
    function restore() { node.nodeValue = text; }
    return g.to(proxy, {
      v: target,
      duration: opts.duration != null ? opts.duration : 1.1,
      ease: 'power2.out',
      onUpdate: function () { node.nodeValue = m[1] + proxy.v.toLocaleString('en-US', fmt) + m[3]; },
      onComplete: restore,
      onReverseComplete: restore
    });
  }

  /* ---------------------------------------------------------------------
     E10 scramble. Decorative mono labels only (aria-hidden), plain text only.
     Resolves left to right into the real text over .45 s.
     --------------------------------------------------------------------- */
  function scramble(el, opts) {
    var g = G();
    opts = opts || {};
    if (!el || el.children.length) return noop();
    var hidden = el.getAttribute('aria-hidden') === 'true' || (el.closest && el.closest('[aria-hidden="true"]'));
    if (!hidden) return noop();
    var text = el.textContent;
    var proxy = { p: 0 };
    function restore() { el.textContent = text; }
    return g.to(proxy, {
      p: 1,
      duration: opts.duration != null ? opts.duration : 0.45,
      ease: 'none',
      onUpdate: function () {
        var n = Math.floor(proxy.p * text.length);
        var out = '';
        for (var i = 0; i < text.length; i++) {
          var ch = text.charAt(i);
          out += (i < n || !/[A-Za-z0-9]/.test(ch)) ? ch : CHARS.charAt(Math.floor(Math.random() * CHARS.length));
        }
        el.textContent = out;
      },
      onComplete: restore,
      onReverseComplete: restore
    });
  }

  /* ---------------------------------------------------------------------
     Pause motion (WCAG 2.2.2). One control type, any number of buttons.
     --------------------------------------------------------------------- */
  function paused() { return pausedNow || loadedPaused; }

  function pauseVideos(on) {
    // The brand-page header loop autoplays with no controls; it obeys the pause too.
    each(document, 'video.sd-coll__video', function (v) {
      if (on) { try { v.pause(); } catch (e) { /* nothing to do */ } }
      else {
        var p = v.play && v.play();
        if (p && p.catch) p.catch(function () { /* autoplay refused: stays still */ });
      }
    });
  }

  function syncToggles() {
    var p = paused();
    each(document, '[data-sd-motion-toggle]', function (b) {
      if (toggleMode === 'hidden') { b.hidden = true; return; }
      b.hidden = false;
      // One pattern only: the label names what a press does. aria-pressed on
      // top of a swapped label would announce "Play motion, pressed".
      b.removeAttribute('aria-pressed');
      if (p) b.setAttribute('data-sd-paused', ''); else b.removeAttribute('data-sd-paused');
      var label = b.querySelector('[data-sd-motion-label]') || b;
      label.textContent = p ? 'Play motion' : 'Pause motion';
    });
  }

  function setPaused(on) {
    on = !!on;
    pausedNow = on;
    if (!on) loadedPaused = false;
    window.sdMotionPaused = on;
    try {
      if (on) localStorage.setItem(KEY, 'off');
      else localStorage.removeItem(KEY);
    } catch (e) { /* private mode: this page view still pauses */ }
    ROOT.classList.toggle('sd-v4-paused', on);
    syncToggles();
    pauseVideos(on);
    var ev;
    try {
      ev = new CustomEvent('sd:motion-pause', { detail: { paused: on } });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent('sd:motion-pause', false, false, { paused: on });
    }
    document.dispatchEvent(ev);
  }

  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-sd-motion-toggle]') : null;
    if (!b) return;
    e.preventDefault();
    if (toggleMode === 'stored') {
      // The page loaded without motion because of the stored pause: the only
      // way to get it back is a fresh load without the key.
      try { localStorage.removeItem(KEY); } catch (err) { /* ignore */ }
      window.location.reload();
      return;
    }
    if (toggleMode === 'run') setPaused(!paused());
  });

  if (loadedPaused) {
    ROOT.classList.add('sd-v4-paused');
    window.sdMotionPaused = true;
    pauseVideos(true);
  }

  /* ---------------------------------------------------------------------
     Header offset (SPEC 5.4). The header scrolls away today, so the default
     0px is right; this only switches on if it is ever made to really stick.
     --------------------------------------------------------------------- */
  function headerCheck() {
    if ((window.scrollY || window.pageYOffset || 0) < 400) return;
    window.removeEventListener('scroll', headerCheck);
    var h = document.querySelector('[data-sd-header]');
    if (!h) return;
    var r = h.getBoundingClientRect();
    if (r.top <= 0.5 && r.bottom > 0) {
      ROOT.style.setProperty('--sd-v4-hdr', Math.round(r.height) + 'px');
      refresh();
    }
  }
  window.addEventListener('scroll', headerCheck, { passive: true });

  /* ---------------------------------------------------------------------
     PDP axes strip: the glyph performs each lit axis once (E8, scaled to a
     120 x 80 drawing), the matching chip pulses, then it rests. Max 3.6 s,
     so no pause control is needed; Replay is offered afterwards.
     --------------------------------------------------------------------- */
  var GEST = {
    pitch: function (tl, d, t) { tl.to(d, { rotation: -5, duration: 0.3, yoyo: true, repeat: 1 }, t); },
    roll: function (tl, d, t) { tl.to(d, { rotation: 5, duration: 0.3, yoyo: true, repeat: 1 }, t); },
    yaw: function (tl, d, t) { tl.to(d, { scaleX: 0.9, x: -3, duration: 0.3, yoyo: true, repeat: 1 }, t); },
    heave: function (tl, d, t) { tl.to(d, { y: -5, duration: 0.3, yoyo: true, repeat: 1 }, t); },
    surge: function (tl, d, t) { tl.to(d, { x: 4, scale: 1.03, duration: 0.3, yoyo: true, repeat: 1 }, t); },
    sway: function (tl, d, t) {
      tl.to(d, { x: -4, duration: 0.15 }, t)
        .to(d, { x: 4, duration: 0.3 }, t + 0.15)
        .to(d, { x: 0, duration: 0.15 }, t + 0.45);
    }
  };

  function axesStrip(strip) {
    if (strip.hasAttribute('data-sd-axes-init')) return;
    strip.setAttribute('data-sd-axes-init', '');
    var g = G();
    var deck = strip.querySelector('.sd-axstrip__deck');
    var replay = strip.querySelector('.sd-axstrip__replay');
    var list = (strip.getAttribute('data-sd-axes') || '').split(',').filter(function (a) { return GEST[a]; });
    if (!deck || !list.length) return;
    var chips = Array.prototype.slice.call(strip.querySelectorAll('.sd-axstrip__chip'));
    function mark(ax) {
      chips.forEach(function (c) { c.classList.toggle('is-now', c.getAttribute('data-axis') === ax); });
    }
    function done() {
      mark('');
      if (replay) replay.hidden = false;
    }
    g.set(deck, { transformOrigin: '50% 100%' });
    var tl = g.timeline({ paused: true, defaults: { ease: 'sine.inOut' }, onComplete: done });
    list.forEach(function (ax, i) {
      var t = i * 0.6;
      var chip = strip.querySelector('.sd-axstrip__chip[data-axis="' + ax + '"]');
      tl.call(mark, [ax], t);
      GEST[ax](tl, deck, t);
      if (chip) tl.fromTo(chip, { scale: 1 }, { scale: 1.1, duration: 0.15, yoyo: true, repeat: 1, ease: 'power2.out' }, t);
    });
    if (replay) replay.addEventListener('click', function () { tl.restart(); });
    document.addEventListener('sd:motion-pause', function (e) {
      if (e.detail && e.detail.paused && tl.isActive()) tl.progress(1).pause();
    });
    track(strip, ST().create({
      trigger: strip,
      start: 'top 85%',
      once: true,
      onEnter: function () {
        if (paused()) { done(); return; }
        tl.play(0);
      }
    }));
  }

  /* ---------------------------------------------------------------------
     Declarative behaviours (after ready, GSAP proven)
     --------------------------------------------------------------------- */
  function declare(root) {
    var S = ST();

    each(root, '[data-sd-split]', function (el) {
      if (el.hasAttribute('data-sd-split-init')) return;
      el.setAttribute('data-sd-split-init', '');
      if (!belowFold(el)) return;
      split(el); // DOM only; nothing changes on screen
      track(el, S.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        // Built at the moment it plays: every word's from-state is written in
        // the same frame the slam starts, never earlier. A paused visitor just
        // gets the heading, already in place.
        onEnter: function () { if (!paused()) slam(el, { immediateRender: true }); }
      }));
    });

    each(root, '[data-sd-draw]', function (el) {
      if (el.hasAttribute('data-sd-draw-init')) return;
      el.setAttribute('data-sd-draw-init', '');
      if (!belowFold(el)) return;
      var L = prepDraw(el); // decorative stroke, below the fold: pre-state is safe
      if (!(L > 0)) { el.style.strokeDasharray = ''; el.style.strokeDashoffset = ''; return; }
      track(el, G().to(el, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: 'power2.inOut',
        scrollTrigger: { trigger: el, start: 'clamp(top 85%)', once: true }
      }));
    });

    each(root, '[data-sd-scramble]', function (el) {
      if (el.hasAttribute('data-sd-scramble-init')) return;
      el.setAttribute('data-sd-scramble-init', '');
      track(el, S.create({
        trigger: el,
        start: 'top 90%',
        once: true,
        onEnter: function () { if (!paused()) scramble(el); }
      }));
    });

    each(root, '[data-sd-axes]', axesStrip);
  }

  /* ---------------------------------------------------------------------
     Refresh hygiene: late fonts, late images, Judge.me injecting its widget,
     the quiz changing height. Body height changes only, debounced.
     --------------------------------------------------------------------- */
  function hygiene() {
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
    if (document.readyState !== 'complete') window.addEventListener('load', refresh, { once: true });
    if ('ResizeObserver' in window && document.body) {
      var lastH = document.body.offsetHeight;
      var t = 0;
      new ResizeObserver(function () {
        var h = document.body.offsetHeight;
        if (Math.abs(h - lastH) < 2) return;
        lastH = h;
        clearTimeout(t);
        t = setTimeout(function () { if (M) M.ScrollTrigger.refresh(); }, 200);
      }).observe(document.body);
    }
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  ready.then(function (m) {
    M = (m && m.gsap && m.ScrollTrigger) ? m : null;
    var blocked = window.sdMotionBlockedBy ? window.sdMotionBlockedBy() : (loadedPaused ? 'user' : null);
    toggleMode = M ? 'run' : (blocked === 'user' ? 'stored' : 'hidden');
    syncToggles();
    if (!M) return;
    declare(document);
    hygiene();
    refresh();
  });

  document.addEventListener('shopify:section:load', function (e) {
    syncToggles();
    if (!M) return;
    declare(e.target);
    refresh();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    registry = registry.filter(function (r) {
      if (e.target.contains(r.el)) { r.kill(); return false; }
      return true;
    });
  });
})();
